/**
 * Input contracts (ARCHITECTURE §8 "Input"). Devices turn physical state into an InputFrame; the InputManager
 * maps devices to player slots and is sampled ONCE per sim tick.
 */
import type { ButtonName, InputFrame } from '@hf/shared';

export type DeviceKind = 'keyboard' | 'gamepad' | 'touch' | 'cpu' | 'replay';

/**
 * THE device id convention (InputDevice.id, SlotSetup.deviceIds, Settings.bindings keys). Use these, never
 * hand-written strings.
 *   kb:solo  one player on the keyboard (both layouts drive the same slot)
 *   kb:p1    left half of a shared keyboard (WASD + J/K)
 *   kb:p2    right half (arrows + Numpad1/2 or . /)
 *   pad:<n>  gamepad index n (Gamepad API)
 *   pad:*    every connected pad not explicitly assigned (`pad:<n>`) to the OTHER slot — for a slot that is the
 *            only human, so a controller enumerating at another index still plays. Never a binding key.
 *   touch    on-screen controls
 */
export const DEVICE_ID = {
  KB_SOLO: 'kb:solo',
  KB_P1: 'kb:p1',
  KB_P2: 'kb:p2',
  TOUCH: 'touch',
  ANY_PAD: 'pad:*',
  pad: (index: number): string => `pad:${index}`,
} as const;

/** `pad:3` → 3, anything else → -1. */
export const padIndexOf = (deviceId: string): number => {
  if (!deviceId.startsWith('pad:')) return -1;
  const n = Number(deviceId.slice(4));
  return Number.isInteger(n) && n >= 0 ? n : -1;
};

export interface InputDevice {
  /** Stable id from DEVICE_ID. */
  readonly id: string;
  readonly kind: DeviceKind;
  readonly label: string;
  /** Is it currently usable (pad connected, touch visible…)? */
  readonly connected: boolean;
  /**
   * Return the frame for this tick. MUST include buttons that were pressed and released since the last sample
   * (tap latching) so sub-tick taps are never lost. Called exactly once per tick. Never allocates.
   */
  sample(): InputFrame;
  /** Remove listeners. */
  dispose(): void;
}

/**
 * Logical button → physical codes. Keyboards: `KeyboardEvent.code` ("KeyJ", "ArrowLeft"). Gamepads (standard
 * mapping): button index as a string ("0".."16") or a stick direction "axis<N>-" / "axis<N>+" (axis 0/1 = left
 * stick, processed with a radial deadzone; other axes with a per-axis threshold). Touch: fixed layout.
 */
export type Bindings = Record<ButtonName, string[]>;

export type TouchMode = 'auto' | 'on' | 'off';

export interface CaptureOptions {
  /** Give up after this long (ms). Default 10 s. Resolves null. */
  timeoutMs?: number;
  /** Abort (resolves null). */
  signal?: AbortSignal;
}

/** Where a menu action came from (the match scene pauses on keyboard Escape but not on a pad's B button). */
export interface MenuSource {
  deviceId: string;
  kind: DeviceKind;
}

/**
 * Auxiliary in-match commands that are not game buttons (ignored when the key is bound to a game button):
 *   select  keyboard R · pad Back/Select      practice: re-spot the ball · tutorial: retry the step
 *   swap    keyboard Tab · pad right-stick click   practice: switch offense/defense
 *   skip    keyboard N                         tutorial: skip the step
 */
export type AuxCommand = 'select' | 'swap' | 'skip';

/** Touch controls status (Settings screen, HUD layout). */
export interface TouchStatus {
  /** Settings.touchControls as applied. */
  readonly mode: TouchMode;
  /** A touch screen is in use / forced on (the `touch` device exists). */
  readonly available: boolean;
  /** The on-screen pad is shown right now (a slot uses `touch` and nothing suspends it). */
  readonly visible: boolean;
}

export interface InputManager {
  /** All known devices (keyboard layouts, connected pads, touch). The array is replaced on hot-plug. */
  devices(): readonly InputDevice[];
  /** Fires after a pad connects/disconnects or touch becomes available. */
  onDevicesChanged(handler: (devices: readonly InputDevice[]) => void): () => void;
  /**
   * Assign devices to a player slot (0/1). A slot may have several devices (keyboard + pad). Ids are kept, so a
   * pad that is plugged in later starts driving the slot as soon as it connects.
   */
  assign(slot: 0 | 1, deviceIds: readonly string[]): void;
  /** Device ids currently assigned to the slot. */
  assigned(slot: 0 | 1): readonly string[];
  /** Sample the slot's devices (OR-ed together) for this tick. A slot with no devices samples 0. */
  sample(slot: 0 | 1): InputFrame;
  /** Call once per sim tick AFTER sampling every slot: clears tap latches. */
  endTick(): void;
  /**
   * Menu navigation edges (DOM UI): fired from any device. Keyboard: arrows/WASD = directions, Enter/Space/J =
   * confirm, Escape/Backspace = back, P = pause. Pads: D-pad/left stick (with auto-repeat) = directions, A/✕ =
   * confirm, B/○ = back, Start = pause. Touch: the on-screen pause button. In a match, the match scene treats a
   * keyboard 'back' and any 'pause' as pause.
   */
  onMenu(handler: (action: MenuAction, source: MenuSource) => void): () => void;
  /** In-match auxiliary commands (see AuxCommand). */
  onAux(handler: (command: AuxCommand, source: MenuSource) => void): () => void;
  /** Rebinding: replace a device's bindings (the caller persists them in `Settings.bindings[deviceId]`). */
  setBindings(deviceId: string, bindings: Bindings): void;
  /** Restore a device's default bindings. */
  resetBindings(deviceId: string): void;
  /** The bindings currently used by a device (defaults when not rebound). */
  bindingsFor(deviceId: string): Bindings;
  /** Factory defaults for a device id. */
  defaultBindings(deviceId: string): Bindings;
  /**
   * Apply a persisted `Settings.bindings` record (untrusted: sanitized): listed devices get their bindings, every
   * other device returns to its defaults. Call at boot and whenever the settings change.
   */
  loadBindings(saved: Readonly<Record<string, unknown>> | null | undefined): void;
  /**
   * Rebinding UI: resolve with the next physical code pressed on that device (keyboard: a KeyboardEvent.code;
   * pad: a button index or "axisN±"), or null on cancel / timeout / abort. Cancel: keyboard Escape (any device), pad
   * Start, or pad B held CAPTURE_CANCEL_HOLD_MS (a B tap binds B). Menu events are suppressed while capturing.
   */
  captureBinding(deviceId: string, options?: CaptureOptions): Promise<string | null>;
  /** Human label for a code on a device: "KeyJ" → "J", pad "0" → "A" (or "✕" on PlayStation pads). */
  labelFor(deviceId: string, code: string): string;
  /** Labels of the first code bound to each logical button, for prompts ("press J to snap"). */
  promptFor(deviceId: string, button: ButtonName): string;
  /** The device that produced the most recent input (prompts follow it), or null before any input. */
  lastActiveDevice(): string | null;
  /** Touch overlay visibility policy (Settings.touchControls). */
  setTouchMode(mode: TouchMode): void;
  /** Live touch-controls status. */
  readonly touch: TouchStatus;
  /**
   * Fires when the touch status changes (mode, availability or the overlay's visibility), with the live status.
   * The HUD lays itself out from it instead of polling `touch`.
   */
  onTouchChanged(handler: (status: TouchStatus) => void): () => void;
  /** Hide the touch overlay temporarily (pause menu open) without changing the mode. */
  setTouchSuspended(suspended: boolean): void;
  /** Rumble the pads assigned to a slot (0..1 strength). No-op without haptics. */
  rumble(slot: 0 | 1, strength: number, durationMs: number): void;
  dispose(): void;
}

export type MenuAction = 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back' | 'pause';
